import { Controller } from "@hotwired/stimulus"

// Live SLA countdown.
// Reads server-rendered absolute timestamps + paused state from data attrs and
// re-renders the timer text every second on the client. No backend calls.
//
// Data attributes (all on the controller's root element):
//   data-support--sla-timer-due-at-value           — ISO8601 deadline (already pause-adjusted on render)
//   data-support--sla-timer-paused-value           — "true" | "false"
//   data-support--sla-timer-resolved-value         — "true" | "false"
//   data-support--sla-timer-total-seconds-value    — total SLA budget in seconds (priority.sla_resolution_hours * 3600)
//   data-support--sla-timer-warning-threshold-value — fraction (default 0.2) below which we flip to WARNING
export default class extends Controller {
  static targets = ["timer", "subtext"]
  static values = {
    dueAt: String,
    paused: Boolean,
    resolved: Boolean,
    totalSeconds: Number,
    warningThreshold: { type: Number, default: 0.2 },
    breachedLabel: { type: String, default: "OVERDUE" },
    onTrackLabel: { type: String, default: "On track" },
    dueSoonLabel: { type: String, default: "Approaching breach" },
    remainingLabel: { type: String, default: "remaining" },
  }

  connect() {
    if (this.pausedValue || this.resolvedValue) return // server-rendered text is final
    this.tick()
    this.interval = setInterval(() => this.tick(), 1000)
  }

  disconnect() {
    if (this.interval) clearInterval(this.interval)
  }

  tick() {
    if (!this.hasTimerTarget) return
    const dueAt = Date.parse(this.dueAtValue)
    if (Number.isNaN(dueAt)) return

    const remainingSec = Math.floor((dueAt - Date.now()) / 1000)
    const breached = remainingSec < 0
    const formatted = this.formatDuration(remainingSec)

    this.timerTarget.textContent = breached ? `-${formatted}` : formatted

    if (this.hasSubtextTarget) {
      let label = this.remainingLabelValue
      if (breached) label = this.breachedLabelValue
      else if (
        this.totalSecondsValue > 0 &&
        remainingSec < this.totalSecondsValue * this.warningThresholdValue
      ) {
        label = this.dueSoonLabelValue
      } else {
        label = this.onTrackLabelValue
      }
      this.subtextTarget.textContent = label
    }
  }

  formatDuration(seconds) {
    const total = Math.abs(seconds)
    const d = Math.floor(total / 86400)
    const h = Math.floor((total % 86400) / 3600)
    const m = Math.floor((total % 3600) / 60)
    if (d > 0) return `${d}d ${h}h`
    if (h > 0) return `${h}h ${m}m`
    return `${m}m`
  }
}
